/**
 * The current view as CSV (REQ-WEB-011, clarified 2026-09-24).
 *
 * "The view" is the query, not the rows paged in: D7 says paging position is
 * not view state, so the file holds every lens matching the current camera,
 * filters, columns and sort — fetched here in full — whether or not it has been
 * scrolled onto the screen.
 *
 * Each cell is the text the screen shows, through the same `formatCell`, so the
 * file and the table cannot disagree about rounding, units or enum labels. The
 * one difference is deliberate: an unknown value is an empty cell, not `—`. A
 * dash in a numeric column breaks every spreadsheet that reads it, and an empty
 * cell is how every tool already spells "no value" — never zero (REQ-WEB-007).
 *
 * Every row carries the licence and credit (REQ-WEB-016), the view it came
 * from and when, as the last three columns (REQ-OPS-015 as amended by D26):
 * the header is the first line, so every CSV reader parses it as it is.
 */
import type { Attribute, LensPage, LensRow } from "./api";
import { UNKNOWN, formatCell, lensName } from "./format";
import { ATTRIBUTION } from "./pages";
import { labelWithUnit } from "./query";

/** RFC 4180: quote when a field holds a comma, quote, or line break. */
export function csvField(text: string): string {
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function csvCell(attr: Attribute | undefined, row: LensRow, key: string): string {
  const text = formatCell(attr, row.values[key]);
  return text === UNKNOWN ? "" : text;
}

export function toCsv(
  rows: LensRow[],
  columns: string[],
  byKey: Map<string, Attribute>,
  viewUrl: string,
  generatedAt: Date,
): string {
  // REQ-OPS-015 as amended by D26 (#223): the licence, the view and the time
  // travel as columns on every row, not as `#` lines, which some CSV readers
  // take as the header. The header is the first line.
  const licence = `CC BY 4.0 https://creativecommons.org/licenses/by/4.0/; ${ATTRIBUTION}`;
  const generated = generatedAt.toISOString();
  const lines = [
    ["Lens", ...columns.map((k) => labelWithUnit(byKey.get(k), k)), "Licence", "View", "Generated"]
      .map(csvField).join(","),
    ...rows.map((r) =>
      [
        lensName(r.manufacturer_name, r.model_name),
        ...columns.map((k) => csvCell(byKey.get(k), r, k)),
        licence,
        viewUrl,
        generated,
      ]
        .map(csvField)
        .join(","),
    ),
  ];
  // CRLF per RFC 4180; Excel and every parser that matters accept it.
  return lines.join("\r\n") + "\r\n";
}

/**
 * Every row matching `query`, following the cursor to the end.
 *
 * 200 is the API's page-size ceiling (REQ-API-018), so the whole corpus is a
 * dozen requests rather than eighty. The total is checked at the end: a file
 * short of the count it claims is worse than no file.
 */
export async function fetchAll(
  query: string,
  get: (q: string) => Promise<LensPage>,
): Promise<LensRow[]> {
  const rows: LensRow[] = [];
  let cursor: string | null = null;
  let total = 0;
  for (let guard = 0; guard < 1000; guard++) {
    const page: LensPage = await get(cursor ? `${query}&cursor=${encodeURIComponent(cursor)}` : query);
    rows.push(...page.items);
    total = page.total;
    if (!page.has_more || !page.next_cursor) break;
    cursor = page.next_cursor;
  }
  if (rows.length !== total) {
    throw new Error(`Fetched ${rows.length} of ${total} matching lenses; the export was not saved.`);
  }
  return rows;
}

/** Hand the file to the browser. */
export function saveCsv(text: string, name: string): void {
  // The BOM tells Excel the file is UTF-8, so "ƒ/2.8" and "×" survive opening it.
  const blob = new Blob(["﻿", text], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
